/**
 * Read-only monthly activity calendar for one aircraft (#803).
 *
 * Shows which days the aircraft actually flew, so activity can be seen at a
 * glance instead of by drawing tracks one window at a time. Display only: no
 * day is clickable and nothing is edited.
 *
 * It opens on the current month and pages backwards freely; forwards it stops
 * at the current month, because there is no future activity to show.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { getBackendUrl } from '../../../services/configService';
import { todayInNwt } from '../../../utils/nwtDate';
import { shiftMonth, monthGrid, canGoForward, monthLabel } from './affActivityMonth';
import './AffActivityCalendar.scss';

interface AffActivityCalendarProps {
  esn: string;
}

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export const AffActivityCalendar: React.FC<AffActivityCalendarProps> = ({ esn }) => {
  // "Current" is the NWT month, matching the days the backend buckets by.
  const currentMonth = useMemo(() => todayInNwt().slice(0, 7), []);
  const [month, setMonth] = useState<string>(currentMonth);
  const [activeDays, setActiveDays] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    // The whole load is inside the try, getBackendUrl included: it THROWS when
    // config has not loaded, and a calendar that cannot reach the backend must
    // report that itself rather than taking the aircraft panel down with it.
    (async () => {
      try {
        // credentials matter: off localhost the backend is a different origin,
        // so without this the request 401s (the trap #779 hit).
        const response = await fetch(
          `${getBackendUrl()}/api/aff/activity/${encodeURIComponent(esn)}?month=${month}`,
          { credentials: 'include' },
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data = await response.json();
        if (cancelled) return;
        setActiveDays(new Set<string>(data?.activeDays ?? []));
      } catch {
        if (cancelled) return;
        // Say nothing was loaded rather than showing an empty month, which is
        // indistinguishable from an aircraft that never flew.
        setActiveDays(new Set());
        setError('Activity could not be loaded');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [esn, month]);

  const weeks = useMemo(() => monthGrid(month), [month]);
  const forward = canGoForward(month, currentMonth);

  return (
    <div className="aff-activity">
      <div className="aff-activity__header">
        <button
          type="button"
          className="aff-activity__nav"
          onClick={() => setMonth(shiftMonth(month, -1))}
          aria-label="Previous month"
        >
          ‹
        </button>
        <span className="aff-activity__month">{monthLabel(month)}</span>
        <button
          type="button"
          className="aff-activity__nav"
          onClick={() => forward && setMonth(shiftMonth(month, 1))}
          disabled={!forward}
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      {error && <div className="aff-activity__error">{error}</div>}

      <div className="aff-activity__grid" aria-busy={loading}>
        {WEEKDAYS.map((day) => (
          <div key={day} className="aff-activity__weekday">{day}</div>
        ))}

        {weeks.flat().map((date, index) => (
          <div
            key={date ?? `pad-${index}`}
            className={`aff-activity__day${date ? '' : ' aff-activity__day--pad'}`}
            data-date={date ?? undefined}
          >
            {date && <span className="aff-activity__num">{Number(date.slice(8))}</span>}
            {date && activeDays.has(date) && (
              <span className="aff-activity__dot" title="Flew this day" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AffActivityCalendar;
